import type { IBatchStore } from './batch-store-types';
import logger from '../../utils/logger';
import { OUTPUT_TTL } from '../../constants/Pac';
import { isFeatureEnabled } from '../../utils/feature-flags';
import { timerKey } from '../../utils/timer-manager/timer-manager';
import { ProxyCommands } from '../../constants/ProxyCommands';
import {
  asyncForEach,
  waitFor,
} from '../../utils/async';

const FEATURE_LINKED_PNO = isFeatureEnabled('linked_pno');

export const pacCommunicationMixin = {

  /**
   * @example
   * set-output 21=500000
   *
   * @param port
   * @param value
   */
  setOutput(this: IBatchStore, port: number|string, value: number|string = '1') {
    logger.log(`set-output ${port}=${value === '1' ? OUTPUT_TTL : value}`);
    const pac = this.isTestMode ? this.pacSimulator : this.pac;

    const linkedPort = FEATURE_LINKED_PNO ? this.devicesStore.linkedPNos.get(port) : undefined;
    if (this.isContaminationDetected(port, value) || (linkedPort && this.isContaminationDetected(linkedPort, value))) {
      return;
    }

    this.sendLinkedOutput(port, value);
    return pac.runWS(`set-output ${port}=${value === '1' ? OUTPUT_TTL : value}`);
  },

  sendLinkedOutput(this: IBatchStore, port: number|string, value: number|string) {
    if (!FEATURE_LINKED_PNO) {
      return;
    }

    const pac = this.isTestMode ? this.pacSimulator : this.pac;
    const { linkedPNos } = this.devicesStore;
    const linkedPort = linkedPNos.get(port);

    if (!linkedPort) {
      return;
    }

    logger.log(`linked: set-output ${linkedPort}=${value}`);

    pac.runWS(`set-output ${linkedPort}=${value === '1' ? OUTPUT_TTL : value}`);
  },

  /**
   * @example
   * set-outputs 17=100000 19=100000 23=105000
   *
   * @param data
   */
  setOutputs(this: IBatchStore, data: { port: number|string, value: number|string }[]) {
    let outputs = '';
    data.forEach(v => {
      if (v.port) {
        outputs += `${v.port}=${v.value} `;
        this.sendLinkedOutput(v.port, v.value);
      }
    });

    logger.log(`set-outputs ${outputs}`);
    const pac = this.isTestMode ? this.pacSimulator : this.pac;

    return pac.runWS(
      `set-outputs ${outputs}`,
    );
  },

  /**
   * @example
   * set-weight + 1 1000 2000 1 3
   * set-weight 1
   *
   * @param sign +/-
   * @param scaleInputPort
   * @param secondaryBinCutOff = target - free fall
   * @param primaryBinCutOff = target - free fall
   * @param secondaryBinOutputPort
   * @param primaryBinOutputPort
   */
  setWeight(this: IBatchStore,
    sign: string,
    scaleInputPort: number|string,
    secondaryBinCutOff: number = 0,
    primaryBinCutOff: number = 0,
    secondaryBinOutputPort: number = 0,
    primaryBinOutputPort: number = 0,
  ) {
    if (this.isTestMode) {
      return;
    }

    const command = sign
      ? `set-weight ${sign} ${scaleInputPort} ${secondaryBinCutOff} ${primaryBinCutOff} ${secondaryBinOutputPort} ${primaryBinOutputPort}`
      : `set-weight ${scaleInputPort}`;
    logger.log(command);
    this.pac.runWS(command);
  },

  /**
   * @example
   * reset-weight 1
   *
   * @param inputPNo scale port
   */
  resetWeight(this: IBatchStore, inputPNo: number|string) {
    logger.log(`reset-weight ${inputPNo}`);
    this.pac.runWS(`reset-weight ${inputPNo}`);
  },

  /**
   * Reset indicator - reset scale weight
   *
   * @param inputPNo
   */
  resetIndicator(this: IBatchStore, inputPNo: number|string) {
    logger.log(`reset-indicator ${inputPNo}`);
    this.pac.runWS(`reset-indicator ${inputPNo}`);
  },

  /**
   * @example
   * set-meter 5=100 12=1
   * set-meter 5
   *
   * @param meterPort
   * @param target
   * @param pumpPort
   * @param pumpPortInitial
   */
  setMeter(this: IBatchStore,
    meterPort: number|string,
    target: number,
    pumpPort: number|string,
    pumpPortInitial: number = 1,
  ) {
    if (this.isTestMode) {
      return;
    }

    target = Math.max(target, 0);
    const command = target >= 0
      ? `set-meter ${meterPort}=${target} ${pumpPort}=${pumpPortInitial}`
      : `set-meter ${meterPort}`;

    logger.log(command);
    this.pac.runWS(command);
  },

  /**
   * Set meter inputs
   *
   * @param inputs meterPort[]
   */
  setMeterInputs(this: IBatchStore, inputs: string[] = []) {
    const command = `set-meter-inputs ${inputs.join(' ')}`;

    logger.log(command);
    this.pac.runWS(command);
  },

  /**
   * @example
   * reset-meter 5
   * reset-meter 5 0 0
   *
   * @param meterPNo pump port
   */
  resetMeter(this: IBatchStore, meterPNo: number|string) {
    logger.log(`reset-meter ${meterPNo}`);
    this.pac.runWS(`reset-meter ${meterPNo}`);
  },

  /**
   * Set meter counter to zero
   *
   * @param meterPNo
   */
  resetCounter(this: IBatchStore, meterPNo: number|string) {
    logger.log(`reset-counter ${meterPNo}`);
    this.pac.runWS(`reset-counter ${meterPNo}`);
  },

  /**
   * Set analog
   *
   * @param port
   * @param value
   */
  setAnalog(this: IBatchStore, port: number, value: number): void {
    logger.log(`set-analog ${port} ${value}`);
    this.pac.runWS(`set-analog ${port} ${value}`);
  },

  closeOpenedOutputs(this: IBatchStore, excluded: number[] = []) {
    let outputs = '';
    this.outputs.forEach((v, i) => {
      if (Number(v) > 0 && !excluded.includes(i+1)) {
        outputs += `${i + 1}=0 `;
      }
    });

    if (outputs) {
      logger.log(`set-outputs ${outputs}`);
      this.pac.runWS(`set-outputs ${outputs}`);
    }
  },

  closeAllOutputs(this: IBatchStore): void {
    const params = new Array(96)
      .fill(0)
      .map((_v, key) => `${key + 1}=0`)
      .join(' ');

    logger.log(`set-outputs ${params}`);
    this.pac.runWS(`set-outputs ${params}`);
  },

  sendPacInitialCommands(this: IBatchStore) {
    this.timers.clear(timerKey.pacInitial());

    if (this.isDemo || (this.pacVersion && this.isPacUidValid(this.pacVersion.uid) && this.isPacInitialCommandSent)) {
      this.start();
      return Promise.resolve();
    }

    if (!this.isDemo && !this.pac.isWSOpen()) {

      this.timers.set(timerKey.pacInitial(), () => {
        if (!this.isPacInitialCommandSent) {
          this.sendPacInitialCommands().catch(err => logger.log('sendPacInitialCommands error:', err));
        }
      }, 250, 'Send PAC initial commands - WS not open');

      return Promise.resolve();

    } else if (!this.pacVersion) {

      this.pac.runWS(ProxyCommands.GET_VERSION);
      this.timers.clear(timerKey.version());

      this.timers.set(timerKey.pacInitial(), () => {
        if (!this.isPacInitialCommandSent) {
          this.sendPacInitialCommands().catch(err => logger.log('sendPacInitialCommands error:', err));
        }
      }, 1000, 'Send PAC initial commands - no version');

      return Promise.resolve();

    } else if (this.devicesStore.devices.length) {

      if (!this.canEmit) {
        return Promise.reject(new Error('Observer mode'));
      }

      if (!this.isPacUidValid(this.pacVersion.uid)) {
        return Promise.reject(new Error('PAC version incorrect!'));
      }

      return this.initialCommands().then(() => {
        this.isPacInitialCommandSent = true;
        this.start();
      });

    } else {

      this.timers.set(timerKey.pacInitial(), () => {
        if (!this.isPacInitialCommandSent) {
          this.sendPacInitialCommands().catch(err => logger.log('sendPacInitialCommands error:', err));
        }
      }, 250, 'Send PAC initial commands - no devices');

      return Promise.resolve();

    }

  },

  initialCommands(this: IBatchStore): Promise<void> {
    const inputs = this.devicesStore.pumps
      .map((pump: { meterPNo: string|number})  => Number(pump.meterPNo || 0)) || [];
    const commands = [
      { name: ProxyCommands.RESET_CONTROLLER },
      { name: ProxyCommands.SET_CONTROLLER },
      { name: `${ProxyCommands.SET_METER_INPUTS} ${inputs.join(' ')}` },
      { name: ProxyCommands.SET_ANALOG },
      { name: ProxyCommands.CHECK_IS_ONLINE },
      { name: ProxyCommands.CHECK_FOR_UPDATES },
      { name: ProxyCommands.GET_PROXY_VERSION },
      { name: ProxyCommands.CHECK_FAST_START_STATUS },
    ];

    const start = async () => {
      await asyncForEach(commands, async (command: { name: string }, i: number) => {
        if (command.name === ProxyCommands.SET_ANALOG) {
          await this.resetAnalogOutputs();
        } else {
          this.pac.runWS(command.name);
        }
        logger.log('initial command:', i, command.name);
        await waitFor(100);
      });
    };

    return start();
  },

  /**
   * Reset all pumps counter
   */
  resetCounters(this: IBatchStore): void {
    this.devicesStore.pumps.forEach((pump: { meterPNo: number }) => {
      this.resetCounter(pump.meterPNo);
    });
  },

  resetAnalogOutputs(this: IBatchStore): Promise<any[] | void> {
    const analogSwitches = this.devicesStore.analogSwitches
      .filter((analog: { channelPNo: number }) => analog.channelPNo);

    if (analogSwitches.length) {
      let promises: any[] = [];
      analogSwitches.forEach((analog: { channelPNo: number }) => {
        logger.log(`set-analog ${analog.channelPNo} 5`);
        promises.push(this.pac.runWS(`set-analog ${analog.channelPNo} 5`));
      });
      if (promises.length) {
        return Promise.all(promises);
      }
    }

    return Promise.resolve();
  },

};
